import gsap from "gsap";

/**
 * Oversized age stepper with hold-to-repeat arrows. After submission the
 * number counts to the average, prefixed with the average sign.
 */
export default class AgeInput {
  constructor(element) {
    if (!element) throw new Error("Element is required");
    this.element = element;
    this.input = element.querySelector(".age-input__input");
    this.average = element.querySelector(".age-input__average");
    this.averageSign = element.querySelector(".age-input__average-sign");
    this.averageNumber = element.querySelector(".age-input__average-number");
    this.liveElement = element.querySelector(".age-input__live");
    this.buttons = Array.from(element.querySelectorAll(".age-input__step"));

    if (!this.input || !this.average || !this.buttons.length) {
      throw new Error("Required elements are missing in age input");
    }

    this.minValue = 1;
    this.maxValue = 120;
    this.startValue = 18;
    this.done = false;

    this.init();
  }

  init() {
    this.buttons.forEach((button) => {
      const direction = button.classList.contains("age-input__step--up")
        ? 1
        : -1;

      button.addEventListener("pointerdown", (event) => {
        event.preventDefault();
        this.step(direction);
        this.holdDelay = setTimeout(() => {
          this.holdInterval = setInterval(() => this.step(direction), 120);
        }, 400);
      });

      ["pointerup", "pointerleave", "pointercancel"].forEach((type) => {
        button.addEventListener(type, () => this.stopHold());
      });

      button.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" && event.key !== " ") return;
        event.preventDefault();
        this.step(direction);
      });
    });

    this.input.addEventListener("change", () => this.clampValue());

    this.input.addEventListener("keydown", (event) => {
      if (this.done) return;
      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.preventDefault();
        this.step(event.key === "ArrowUp" ? 1 : -1);
      } else if (event.key === "Enter") {
        this.clampValue();
        document.querySelector(".big-button--check")?.click();
      }
    });

    this.input.addEventListener("showAverage", (event) =>
      this.showAverage(event)
    );
  }

  clampValue() {
    if (this.input.value === "") return;
    this.input.value = Math.min(
      this.maxValue,
      Math.max(this.minValue, Math.round(Number(this.input.value)))
    );
  }

  stopHold() {
    clearTimeout(this.holdDelay);
    clearInterval(this.holdInterval);
  }

  /* Anton has no tabular figures, so each digit gets a fixed 1ch cell */
  renderDigits(value) {
    const digits = String(value).split("");
    if (this.averageNumber.childElementCount !== digits.length) {
      this.averageNumber.innerHTML = digits
        .map((digit) => `<span class="age-input__digit">${digit}</span>`)
        .join("");
    } else {
      digits.forEach((digit, index) => {
        this.averageNumber.children[index].textContent = digit;
      });
    }
  }

  step(direction) {
    if (this.done) return;

    if (this.input.value === "") {
      if (direction === 1) this.input.value = this.startValue;
    } else {
      const next = Number(this.input.value) + direction;
      if (next < this.minValue) {
        this.input.value = "";
      } else {
        this.input.value = Math.min(this.maxValue, next);
      }
    }

    this.input.dispatchEvent(new Event("input", { bubbles: true }));

    if (!window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      gsap.fromTo(
        this.element.querySelector(".age-input__value"),
        { scale: 1.08 },
        { scale: 1, duration: 0.16, ease: "power2.out" }
      );
    }
  }

  showAverage(event) {
    this.done = true;
    this.stopHold();
    this.input.disabled = true;

    const reducedMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)"
    ).matches;

    const tl = gsap.timeline({ delay: reducedMotion ? 0 : 0.2 });

    tl.to(this.buttons, {
      autoAlpha: 0,
      duration: reducedMotion ? 0 : 0.25,
      ease: "power2.out",
    });

    const average = event.detail.average;
    if (typeof average !== "number" || this.input.value === "") return;

    const counter = { value: Number(this.input.value) };
    this.renderDigits(Math.round(counter.value));

    tl.set(this.input, { display: "none" }, 0);
    tl.set(this.average, { display: "block" }, 0);
    tl.fromTo(
      this.averageSign,
      { autoAlpha: 0, scale: 0.5 },
      {
        autoAlpha: 1,
        scale: 1,
        duration: reducedMotion ? 0 : 0.4,
        ease: "back.out(2)",
      },
      0
    );
    tl.to(
      counter,
      {
        value: average,
        duration: reducedMotion
          ? 0
          : gsap.utils.clamp(1, 2.5, Math.abs(average - counter.value) * 0.06),
        ease: "power2.out",
        onUpdate: () => {
          this.renderDigits(Math.round(counter.value));
        },
      },
      reducedMotion ? ">" : "+=0.15"
    );
    tl.call(() => {
      if (this.liveElement) {
        this.liveElement.textContent = `Durchschnitt: ${Math.round(average)}`;
      }
    });
  }
}
